import { getPaginationOptions } from '@/utils/table'
import { tableListDefaultPageSize } from '@/config'
import { useMount, useSetState, useSize } from 'ahooks'
import { Card, Table, Button, message, Modal, Tooltip } from 'antd'
import { getDataProjectList, deleteDataProject, updateDataProject, addDataProject, getProjectAttrs } from '@/pages/documentCompile/api/data-project'
import styles from './index.module.less'
import dayjs from 'dayjs'
import { useTableData } from '@/hooks/useTableData'
import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import useScreenHeight from '@/hooks/useScreenHeight';
import { margin } from '@/config/theme'
import { formatDate } from '@/utils/date'
import Search from '../search'
import clsx from 'clsx'
import { useAuth } from '@/hooks/useAuth'
import EditModal from '../editModal'
import AddModal from '../addModal'
import ProjectForm from '../addModal/components/projectForm'
import { getDictTableData } from '@/pages/documentCompile/dict/utils'
import { DictData, DictTemplateCodeEnum, DocStatusOptions, DocStatusEnum } from '@/pages/documentCompile/dict/type'
import { getRandowLangUUid, getUUid, isEmpty } from '@/utils/tools'
import { ReactComponent as PrjIcon } from '@/assets/svg/zlbz-zlgc.svg'
import { ResCodeEnum } from '@/utils/request'
import { LockConfig } from '@/config/constant'
import { withResourceLock } from '@/utils/lock'
import ActionButton from '@/components/ui/CustomDropdownButton/ActionButton'
import useGlobalStore from '@/store/useGlobalStore'
import { DocSearchCodes } from '../../constants'
import { reviewDataProject } from '@/api/cloudProject'
import { getStringArray } from '@/utils/arr'

interface DataProjectItem {
    projectId: string | number;
    projectName: string;
    projectType: {
        code: string;
        name: string;
    };
    fileCount: number;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateTime: string;
    docStatus?: string | number;
}

export interface TableStateType {
    pageSize?: number
    pageIndex?: number
    tableData?: DataProjectItem[]
    total?: number
    createOpen?: boolean
    editOpen?: boolean
    curTemp?: any | null
    fieldSettingOpen?: boolean
    dictProjectData?: DictData[]
    projectItemAttrs?: any[],
    // openDialogType 打开入口
    openDialogType?: 'create' | 'edit' | 'detail'
}

const hasSearchValue = value => value !== undefined && value !== null && value !== ''

const formatDateRange = (value?: any[]) => {
    if (!Array.isArray(value) || value.length !== 2) return ''
    const [start, end] = value
    if (!hasSearchValue(start) || !hasSearchValue(end)) return ''
    const formatStr = 'YYYY-MM-DD HH:mm:ss'
    return `${dayjs(start).format(formatStr)},${dayjs(end).format(formatStr)}`
}

const buildSearchParams = (formValues: Record<string, any> = {}) => {
    const projectSearch: any = {}
    const fieldSearches: any[] = []

    const pushFieldSearch = (fieldname: string, operator: string, value: any) => {
        if (!hasSearchValue(value)) return
        fieldSearches.push({ fieldname, operator, value })
    }

    if (hasSearchValue(formValues[DocSearchCodes.projectName])) {
        projectSearch.projectName = formValues[DocSearchCodes.projectName]
    }

    if (hasSearchValue(formValues[DocSearchCodes.docStatus])) {
        projectSearch.docStatus = formValues[DocSearchCodes.docStatus]
    }

    if (hasSearchValue(formValues[DocSearchCodes.projectType])) {
        projectSearch.projectType = formValues[DocSearchCodes.projectType]
    }

    pushFieldSearch(DocSearchCodes.engName, 'like', formValues[DocSearchCodes.engName])
    pushFieldSearch(DocSearchCodes.engCode, 'like', formValues[DocSearchCodes.engCode])
    pushFieldSearch(DocSearchCodes.projectStatus, 'eq', formValues[DocSearchCodes.projectStatus])

    const startDate = formatDateRange(formValues[DocSearchCodes.startDate])
    if (startDate) {
        pushFieldSearch(DocSearchCodes.startDate, 'between', startDate)
    }

    const endDate = formatDateRange(formValues[DocSearchCodes.endDate])
    if (endDate) {
        pushFieldSearch(DocSearchCodes.endDate, 'between', endDate)
    }

    if (fieldSearches.length > 0) {
        projectSearch.fieldSearches = fieldSearches
    }
    if (hasSearchValue(formValues[DocSearchCodes.orgIds])) {
        if (formValues[DocSearchCodes.orgIds]) {
            projectSearch.orgIds = getStringArray(formValues[DocSearchCodes.orgIds])
        }
    }

    return { projectSearch }
}

function DataProjectTable() {
    const { hasAuth, codes } = useAuth()
    // 新建
    const authDelete = useMemo(() => hasAuth('630:doc:prep:proj:remove'), [codes])
    const authEdit = useMemo(() => hasAuth('630:doc:prep:proj:modify'), [codes])
    const authDetail = useMemo(() => hasAuth('630:doc:prep:proj:view'), [codes])
    const navigate = useNavigate()
    const searchParams = useRef<any>({})
    const refPjectDictMap = useRef<any>({})

    const getList = async params => {
        const data = await getDataProjectList(params) as any

        // 遍历 fields 属性，找到某些属性 赋值到最外层 DocSearchCodes.projectStatus 
        (data.result || []).forEach(item => {
            item.fields?.forEach(field => {
                if (field.name == DocSearchCodes.projectStatus) {
                    item[DocSearchCodes.projectStatus] = field.displayValue
                }
            })
        })
        return {
            total: data.totalCount || 0,
            list: data.result || [],
        }
    }

    const { data, loading, pagination, handleTableChange, refresh, search } = useTableData({
        initialParams: {
            pageIndex: 1,
            pageSize: 20,
        },
        service: getList,
    })

    const [state, setState] = useSetState<TableStateType>({
        pageSize: tableListDefaultPageSize,
        pageIndex: 1,
        total: 0,
        createOpen: false,
        editOpen: false,
        curTemp: null,
        tableData: [],
        dictProjectData: [],
        projectItemAttrs: [],
    })

    // 点击资料包名称
    const handleClickProjectName = (record: DataProjectItem,) => {
        const requestId = getRandowLangUUid(5)
        useGlobalStore.setState({
            authListItem: record
        })
        const editable = hasAuth('630:doc:prep:proj:modify', record.createUser) ? 1 : 0
        withResourceLock({
            requestId,
            lockCode: "PROJECT",
            resourceId: record.projectId,
            onLockFree: () => {
                navigate(`/document/engList/detail?projectId=${record.projectId}&name=${record?.projectName}&requestId=${requestId}&editable=${editable}`)
            },
            onLockExists: () => {
                navigate(`/document/engList/detail?projectId=${record.projectId}&name=${record?.projectName}&requestId=${requestId}&editable=0`)
            }
        })
    }

    // 编辑行
    const editRow = async (record: DataProjectItem) => {
        const res = await getProjectAttrs(record.projectId)
        setState({
            editOpen: true,
            curTemp: record,
            projectItemAttrs: res,
            openDialogType: 'edit'
        })
    }
    // 审阅
    const reviewProject = (record: DataProjectItem) => {
        // Modal.confirm({
        //     title: '确认操作',
        //     content: '即将审阅通过，资料状态变为"已完成"！此操作不可撤销，是否确认操作？',
        //     onOk: async () => {
        //         try {
        //             await reviewDataProject({
        //                 projectId: record.projectId,
        //             });
        //             message.success('审阅成功');
        //             console.log("searchParams.current", searchParams.current)
        //             search(searchParams.current || {});
        //         } catch (error) {
        //             console.log("err", error);
        //             message.error('审阅失败');
        //         }
        //     },
        // });
        const requestId = getRandowLangUUid(5)
        useGlobalStore.setState({
            authListItem: record
        })
        const editable = hasAuth('630:doc:prep:proj:modify', record.createUser) ? 1 : 0
        withResourceLock({
            requestId,
            lockCode: "PROJECT",
            resourceId: record.projectId,
            onLockFree: () => {
                navigate(`/document/engList/detail?projectId=${record.projectId}&name=${record?.projectName}&requestId=${requestId}&editable=${editable}&detailReferrer=review`)
            },
            onLockExists: () => {
                navigate(`/document/engList/detail?projectId=${record.projectId}&name=${record?.projectName}&requestId=${requestId}&editable=0&detailReferrer=review`)
            }
        })
    }

    // 删除行
    const deleteRow = (record: DataProjectItem) => {
        Modal.confirm({
            title: `确定删除资料工程 ${record.projectName}？`,
            okText: '确认',
            cancelText: '取消',
            onOk() {
                deleteDataProject([record.projectId]).then(res => {
                    message.success('删除成功')
                    search(searchParams.current || {})
                }).catch(err => {
                    message.error('删除失败')
                })
            },
        })
    }
    const handleDir = (record: DataProjectItem) => {
        const editable = hasAuth('630:doc:prep:proj:modify', record.createUser)
        navigate(`/document/engList/dir?projectId=${record.projectId}&name=${record.projectName}&editable=${editable ? 1 : 0}`)
    }

    const columns: any[] = [
        {
            title: '序号',
            width: 60,
            dataIndex: 'number',
            className: 'text-center',
            render: (_text, _record, index) => {
                const num = (pagination.current - 1) * pagination.pageSize + (index + 1)
                return <span>{num}</span>
            },
        },
        {
            title: '资料包名称',
            width: 200,
            maxWidth: 200,
            ellipsis: true,
            dataIndex: 'projectName',
            render: (v, record: DataProjectItem) => {
                const authDetailTemp = hasAuth('630:doc:prep:proj:view', record.createUser)
                return (
                    <Tooltip title={v}>
                        <div className={clsx(styles.linkWrap)}>
                            <span className={clsx(styles.linkIconCls)}><PrjIcon /></span>
                            <Button disabled={!authDetailTemp} type='link' onClick={() => handleClickProjectName(record)} className={clsx(styles.btnProjectLink, 'a_link_color')}>{v}</Button>
                        </div>
                    </Tooltip>
                )
            },
        },
        {
            title: '工程类型',
            width: 120,
            dataIndex: 'projectType',
            render: (value) => value?.name || '-'
        },
        // 工程状态
        {
            title: '工程状态',
            width: 100,
            dataIndex: 'docStatus',
            render: (value, record: DataProjectItem) => record[DocSearchCodes.projectStatus] || '-'
        },
        // 资料状态
        {
            title: '资料状态',
            width: 100,
            dataIndex: 'docStatus',
            render: (value) => DocStatusOptions.find(item => item.value == value)?.label || '-'
        },
        // 文件数量
        {
            title: '文件数量',
            width: 100,
            dataIndex: 'fileCount',
            align: 'center',
            render: (value, record: DataProjectItem) => value + ' / ' + (record as any).signedFileCount,
        },
        {
            title: '创建人',
            width: 100,
            dataIndex: 'createRealname',
        },
        {
            title: '创建时间',
            width: 160,
            dataIndex: 'createDate',
            render: text => formatDate(text, 'YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '更新人',
            width: 100,
            dataIndex: 'updateRealname',
        },
        {
            title: '更新时间',
            width: 160,
            dataIndex: 'updateTime',
            render: text => formatDate(text, 'YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '操作',
            dataIndex: 'val',
            width: 120,
            key: 'val',
            render: (_text, record: DataProjectItem) => {
                return (
                    <div className='handle-cell' style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <ActionButton
                            authCode='630:doc:prep:proj:modify'
                            ownerId={record.createUser}
                            size='small'
                            type='link'
                            onClick={() => editRow(record)}
                            className='tableOpBtn'
                        >
                            编辑
                        </ActionButton>
                        {/* 目录 */}
                        <ActionButton
                            ownerId={record.createUser}
                            authCode='630:doc:prep:proj:view'
                            size='small'
                            type='link'
                            onClick={() => handleDir(record)}
                            className='tableOpBtn'
                        >
                            卷内目录
                        </ActionButton>
                        <ActionButton
                            ownerId={record.createUser}
                            authCode='630:doc:prep:proj:remove'
                            danger
                            size='small'
                            type='link'
                            onClick={() => deleteRow(record)}
                            className='tableOpBtn'
                        >
                            删除
                        </ActionButton>
                        {/* 审阅 */}
                        {record.docStatus == DocStatusEnum.COMPILING && (
                            <ActionButton
                                ownerId={record.createUser}
                                authCode='630:doc:prep:proj:review'
                                size='small'
                                type='link'
                                onClick={() => reviewProject(record)}
                                className='tableOpBtn'
                            >
                                审阅
                            </ActionButton>
                        )}
                    </div>
                )
            },
        },
    ]

    const handleCreateOk = async (values: any) => {
        try {
            if (state.curTemp) {
                const params = {
                    ...values,
                    projectId: state.curTemp.projectId
                }
                const res = await updateDataProject(params)
                message.success('更新成功')
            } else {
                const res = await addDataProject(values)
                message.success('创建成功')
            }
            setState({ createOpen: false })
            // search(searchParams.current)
            search(searchParams.current || {})
        } catch (error) {
            message.error('操作失败')
        }
    }

    const handleCreateCancel = () => {
        setState({
            curTemp: null,
            createOpen: false
        })
    }

    const handleSearch = params => {
        const newParams = buildSearchParams(params)
        console.log("newParams", newParams)
        searchParams.current = newParams
        search(newParams)
    }

    const { height: screenHeight } = useScreenHeight()
    const containerStyle: React.CSSProperties = {
        height: `${screenHeight}px`
    }

    const windowSize = useWindowSize()
    const tableWrapRef = useRef(null)
    const [srcollHeight, setSrcollHeight] = useState(300)

    const handleNew = () => {
        setState({
            createOpen: true,
            curTemp: null
        })
    }

    const handleJump = () => {
        setState({ editOpen: false })
    }

    const handleFormOk = async (values: any) => {
        // console.log("values", values)
        // 如果是跳过
        // if (isEmpty(values)) {
        //     setState({ editOpen: false })
        //     return
        // }
        const projectAttrs = []
        Object.keys(values).forEach(key => {
            const dict = refPjectDictMap.current[key]
            if (dict) {
                let value = values[key];

                if (dayjs?.isDayjs?.(value)) {
                    value = value.valueOf();
                } else if (value instanceof Date) {
                    value = value.getTime();
                }

                projectAttrs.push({
                    name: dict.dictCode,
                    type: dict.attr_zdlx,
                    value
                });
            }
        })

        const params = {
            projectId: state.curTemp.projectId,
            projectName: values['zlgcmc'], // state.curTemp.projectName,
            projectAttrs
        }
        updateDataProject(params).then(res => {
            message.success('更新成功')
            search(searchParams.current || {})
            setState({ editOpen: false })
        }).catch(err => {
            message.error('更新失败')
            setState({ editOpen: false })
        })
    }

    useEffect(() => {
        let top = 160
        let res = 800
        if (top > 0 && windowSize?.height) {
            const height = windowSize.height - top - margin - 130
            res = height > 0 ? height : 300
        }
        setSrcollHeight(res)
    }, [windowSize?.height])

    useEffect(() => {
        setState({
            tableData: data,
        })
    }, [data])


    useMount(() => {
        getDictTableData(DictTemplateCodeEnum.PROJECT_ATTR).then(res => {
            res.forEach(item => {
                refPjectDictMap.current[item.dictCode] = item
            })
            res.push({ id: getUUid(), dictCode: 'zlgcmc', dictName: '资料包名称', attr_zdlx: 'dwb', sort: 0 })
            res.sort((a, b) => a.sort - b.sort)
            setState({
                dictProjectData: res,
            })
        })
    })

    const bodySize = useSize(document.querySelector('body'));
    const scrollHeight = useMemo(() => {
        // header 64、 top margin 16、面包屑 38、操作栏 68、分页器 59、bottom margin 16、table header 39
        return bodySize?.height - 64 - 54 - 68 - 59 - 16 - 39 - 1
    }, [bodySize])

    return (
        <div className={styles.container} style={containerStyle}>
            <Search onCreate={handleNew} onSearch={handleSearch} loading={loading} />
            <div className={styles.tableWrap} ref={tableWrapRef}>
                <Table
                    rowKey='projectId'
                    bordered
                    columns={columns}
                    dataSource={state.tableData}
                    pagination={{ ...pagination, showTotal: total => `共${total}项数据` }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content', y: scrollHeight }}
                    loading={false}
                    size='small'
                />
            </div>
            {state.createOpen ? <AddModal
                record={state.curTemp}
                // open={state.createOpen}
                open={true}
                onOk={handleCreateOk}
                onCancel={handleCreateCancel}
                isEdit={!!state.curTemp}
            /> : null}
            {/* {state.editOpen ? <EditModal
                record={state.curTemp}
                open={state.editOpen}
                onOk={handleCreateOk}
                onCancel={handleCreateCancel}
                isEdit={!!state.curTemp}
            /> : null} */}
            <ProjectForm
                open={state.editOpen}
                onCancel={() => setState({ editOpen: false })}
                onOk={handleFormOk}
                openDialogType={state.openDialogType}
                // onJump={handleJump}
                record={state.curTemp}
                projectItemAttrs={state.projectItemAttrs}
                dictData={state.dictProjectData} />
        </div>
    )
}

export default DataProjectTable
